// Assets.js - Main Assets page with Clean Modern design
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAssets, deleteAsset, triggerAssetScan } from '../services/assetService';
import AssetList from '../components/Assets/AssetList';
import AddAssetModal from '../components/Assets/AddAssetModal';
import CleanModernLayout from '../components/layouts/CleanModernLayout';
import { track as trackTelemetry } from '../services/telemetryService';
import './Assets.css';

function Assets() {
    const navigate = useNavigate();
    const [assets, setAssets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [deletingAssetId, setDeletingAssetId] = useState(null);
    const [limits, setLimits] = useState({
        tier: 'free',
        domains: { current: 0, limit: 0, remaining: 0 },
        ipRanges: { current: 0, limit: 0, remaining: 0 }
    });

    // Load assets on mount
    const loadAssets = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await getAssets();
            setAssets(data.assets || []);
            setLimits(data.limits || {
                tier: 'free',
                domains: { current: 0, limit: 0, remaining: 0 },
                ipRanges: { current: 0, limit: 0, remaining: 0 }
            });
        } catch (err) {
            console.error('Failed to load assets:', err);
            setError(err.message || 'Failed to load assets');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadAssets();
    }, [loadAssets]);

    // Handle asset deletion
    const handleDelete = async (assetId) => {
        if (!window.confirm('Are you sure you want to delete this asset? All historical data will be deleted.')) {
            return;
        }

        setDeletingAssetId(assetId);
        setError(null);

        try {
            await deleteAsset(assetId);
            await loadAssets();
        } catch (err) {
            if (err.name === 'AbortError') {
                setError('Delete timed out — the operation may still be running. Wait 30 seconds then refresh the page to confirm.');
            } else {
                setError(`Delete failed: ${err.message || 'Unknown error'}`);
            }
        } finally {
            setDeletingAssetId(null);
        }
    };

    // Handle scan trigger
    const handleScanNow = async (assetId) => {
        try {
            // PRD-151 WP7: manual asset scan triggered. Enum-only scanType (the
            // fixed scan kind this handler triggers) — NEVER the assetId/domain.
            try { trackTelemetry('scan_start', { meta: { scanType: 'baseline' } }); } catch (err) { /* best-effort */ }
            await triggerAssetScan(assetId, 'baseline');
            loadAssets();
        } catch (err) {
            setError(err.message || 'Failed to trigger scan');
        }
    };

    // Handle view details
    const handleViewDetails = (assetId, section = null) => {
        const path = section
            ? `/assets/${assetId}#${section}`
            : `/assets/${assetId}`;
        navigate(path);
    };

    // Handle add modal close with refresh
    const handleAddSuccess = () => {
        setShowAddModal(false);
        loadAssets();
    };

    // Check if user can add assets
    const canAddDomains = limits.domains?.remaining > 0;
    const canAddIpRanges = limits.ipRanges?.remaining > 0;
    const canAddAssets = canAddDomains || canAddIpRanges;
    const hasAssetsFeature = limits.domains?.limit > 0 || limits.ipRanges?.limit > 0;

    return (
        <CleanModernLayout activePage="assets">
            <div className="assets-page cm-page-content">
                {/* Page Header */}
                <header className="cm-page-header">
                    <div className="header-left">
                        <h1 className="page-title">Assets</h1>
                        <div className="tier-usage">
                            <span className="usage-item">
                                <span className="usage-label">Domains:</span>
                                <span className="usage-value">
                                    {limits.domains?.current}/{limits.domains?.limit || 0}
                                </span>
                            </span>
                            <span className="usage-divider">•</span>
                            <span className="usage-item">
                                <span className="usage-label">IP Ranges:</span>
                                <span className="usage-value">
                                    {limits.ipRanges?.current}/{limits.ipRanges?.limit || 0}
                                </span>
                            </span>
                        </div>
                    </div>

                    {hasAssetsFeature && (
                        <button
                            className="cm-action-btn primary"
                            onClick={() => setShowAddModal(true)}
                            disabled={!canAddAssets}
                            title={!canAddAssets ? 'Asset limit reached' : 'Add new asset'}
                        >
                            + Add Asset
                        </button>
                    )}
                </header>

                {/* Error Banner */}
                {error && (
                    <div className="cm-error-banner">
                        <span className="error-icon">⚠</span>
                        <span className="error-message">{error}</span>
                        <button className="error-dismiss" onClick={() => setError(null)}>×</button>
                    </div>
                )}

                {/* Upgrade Banner for Free Tier */}
                {!hasAssetsFeature && !loading && (
                    <div className="cm-upgrade-banner">
                        <div className="upgrade-content">
                            <span className="upgrade-icon">🔒</span>
                            <div className="upgrade-text">
                                <strong>Asset Monitoring requires Standard or Pro tier</strong>
                                <p>
                                    Upgrade to continuously monitor your domains and IP ranges for vulnerabilities.
                                </p>
                            </div>
                            <button
                                className="cm-action-btn primary"
                                onClick={() => navigate('/upgrade')}
                            >
                                View Plans
                            </button>
                        </div>
                    </div>
                )}

                {/* Main Content */}
                <main className="assets-content">
                    {loading ? (
                        <div className="cm-loading-state">
                            <div className="loading-spinner"></div>
                            <p>Loading assets...</p>
                        </div>
                    ) : assets.length === 0 && hasAssetsFeature ? (
                        <div className="cm-empty-state">
                            <div className="empty-icon">📡</div>
                            <h3>No Assets Monitored</h3>
                            <p>
                                Add your first domain or IP range to begin continuous security monitoring.
                            </p>
                            <button
                                className="cm-action-btn primary"
                                onClick={() => setShowAddModal(true)}
                            >
                                + Add Your First Asset
                            </button>
                        </div>
                    ) : hasAssetsFeature ? (
                        <AssetList
                            assets={assets}
                            onDelete={handleDelete}
                            onScanNow={handleScanNow}
                            onViewDetails={handleViewDetails}
                            deletingAssetId={deletingAssetId}
                        />
                    ) : null}
                </main>

                {/* Add Asset Modal */}
                {showAddModal && (
                    <AddAssetModal
                        onClose={() => setShowAddModal(false)}
                        onSuccess={handleAddSuccess}
                        limits={limits}
                    />
                )}
            </div>
        </CleanModernLayout>
    );
}

export default Assets;
